import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { useLanguage } from '../contexts/LanguageContext';
import { useModalClose } from '../hooks/useModalClose';

function CombineCheck({ orderId, orderItems, orderData, totalPrice, tableName, orderName, theme, user, onClose, onCombineCompleted }) {
    const { t } = useLanguage();
    useModalClose(onClose);
    const [availableOrders, setAvailableOrders] = useState([]);
    const [selectedOrderId, setSelectedOrderId] = useState('');
    const [selectedOrderItems, setSelectedOrderItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingOrders, setLoadingOrders] = useState(true);
    const [error, setError] = useState('');
    const [allOptions, setAllOptions] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [existingOrderNames, setExistingOrderNames] = useState([]);

    useEffect(() => {
        fetchAvailableOrders();
        // Fetch all options for mapping ids to names
        fetch(`${BACKEND_URL}/api/debug/options`)
            .then(res => res.json())
            .then(data => setAllOptions(data))
            .catch(() => setAllOptions([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (selectedOrderId) {
            fetchSelectedOrderItems();
        } else {
            setSelectedOrderItems([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOrderId]);

    // Helper to get option names from item.options (JSON string or array)
    const getOptionNames = (item, allOptions) => {
        if (!item.options) return null;
        let optionIds = [];
        if (typeof item.options === 'string') {
            try {
                optionIds = JSON.parse(item.options);
            } catch {
                return null;
            }
        } else if (Array.isArray(item.options)) {
            optionIds = item.options;
        }
        if (!Array.isArray(optionIds) || optionIds.length === 0) return null;
        if (!allOptions) return optionIds;
        // Filter out options with selected === 1
        return optionIds
            .map(id => allOptions.find(o => o.id === id))
            .filter(opt => opt && opt.selected !== 1)
            .map(opt => opt.name);
    };

    const fetchAvailableOrders = async () => {
        try {
            setLoadingOrders(true);
            const response = await fetch(`${BACKEND_URL}/api/orders`);
            if (!response.ok) throw new Error('Failed to fetch orders');
            const orders = await response.json();

            // Filter out current order and closed orders
            const filteredOrders = orders.filter(order =>
                order.id !== parseInt(orderId) && order.closed === 0
            );
            setAvailableOrders(filteredOrders);

            // Extract existing order names for validation (including current order)
            const allOpenOrders = orders.filter(order => order.closed === 0);
            const names = allOpenOrders.map(order => order.name.toLowerCase().trim());
            setExistingOrderNames(names);
        } catch (err) {
            console.error('Error fetching orders:', err);
            setError(t('failedToLoadAvailableOrders'));
        } finally {
            setLoadingOrders(false);
        }
    };

    const fetchSelectedOrderItems = async () => {
        try {
            const response = await fetch(`${BACKEND_URL}/api/order-products?order_id=${selectedOrderId}`);
            if (!response.ok) throw new Error('Failed to fetch order items');
            const items = await response.json();
            setSelectedOrderItems(items);
        } catch (err) {
            console.error('Error fetching selected order items:', err);
            setSelectedOrderItems([]);
        }
    };

    const selectedOrder = availableOrders.find(order => order.id === parseInt(selectedOrderId));
    const selectedOrderTotal = selectedOrderItems.reduce((sum, item) => {
        const itemPrice = Number(item.price || 0) * item.quantity;
        const discount = Number(item.discount || 0);
        return sum + itemPrice - discount;
    }, 0);

    const combinedTotal = totalPrice + selectedOrderTotal;

    const handleCombineCheck = async () => {
        if (!selectedOrderId) {
            setError(t('pleaseSelectAnOrderToCombineWith'));
            return;
        }

        setLoading(true);
        setError('');

        try {
            // Move all items from the selected order to the current order
            for (const item of selectedOrderItems) {
                const response = await fetch(`${BACKEND_URL}/api/order-products/${item.id}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        order_id: orderId
                    }),
                });

                if (!response.ok) {
                    throw new Error(t('failedToMoveItemToCurrentOrder', { itemId: item.id }));
                }
            }

            // Delete the now-empty selected order
            const deleteResponse = await fetch(`${BACKEND_URL}/api/orders/${selectedOrderId}`, {
                method: 'DELETE'
            });

            // Note: Even if delete fails, the combine was successful since items were moved
            if (!deleteResponse.ok) {
                console.warn(t('failedToDeleteEmptyOrderButItemsMovedSuccessfully'));
            }

            console.log(t('ordersCombinedSuccessfully'));
            onCombineCompleted();
        } catch (err) {
            console.error('Error combining orders:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div onClick={onClose} style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: theme?.colors?.overlay || 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
        }}>
            <div onClick={(e) => e.stopPropagation()} style={{
                backgroundColor: theme?.colors?.surface || '#fff',
                borderRadius: theme?.borderRadius || 8,
                padding: '2rem',
                maxWidth: '700px',
                width: '90%',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
            }}>
                <h2 style={{
                    color: theme?.colors?.primary || '#1976d2',
                    marginTop: 0,
                    textAlign: 'center'
                }}>
                    {t('combineChecks')}
                </h2>

                {/* Current Order Summary */}
                <div style={{
                    marginBottom: '1.5rem',
                    padding: '1rem',
                    backgroundColor: theme?.colors?.background || '#f5f5f5',
                    borderRadius: theme?.borderRadius || 6,
                    border: `1px solid ${theme?.colors?.border || '#ddd'}`
                }}>
                    <h3 style={{ margin: '0 0 0.5rem 0', color: theme?.colors?.text }}>{t('currentOrder')}</h3>
                    <div style={{ fontSize: '0.9rem', color: theme?.colors?.textSecondary }}>
                        {tableName && <div>{t('table')}: <strong>{tableName}</strong></div>}
                        {orderName && <div>{t('name')}: <strong>{orderName}</strong></div>}
                        <div>{t('items')}: <strong>{orderItems.length}</strong></div>
                        <div>{t('total')}: <strong>${totalPrice.toFixed(2)}</strong></div>
                    </div>
                </div>

                {/* Order Selection */}
                <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{
                        display: 'block',
                        marginBottom: '0.5rem',
                        fontWeight: 'bold',
                        color: theme?.colors?.text
                    }}>
                        {t('selectOrder')}:
                    </label>
                    {loadingOrders ? (
                        <div>{t('loadingAvailableOrders')}</div>
                    ) : (
                        <select
                            value={selectedOrderId}
                            onChange={(e) => setSelectedOrderId(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '0.5rem',
                                borderRadius: theme?.borderRadius || 4,
                                border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                                fontSize: '1rem'
                            }}
                        >
                            <option value="">-- {t('selectOrder')} --</option>
                            {availableOrders.map(order => (
                                <option key={order.id} value={order.id}>
                                    {order.name} - {t('table')}: {order.table_name || t('unknown')} (ID: {order.id})
                                </option>
                            ))}
                        </select>
                    )}
                </div>

                {/* Selected Order Details */}
                {selectedOrder && (
                    <div style={{
                        marginBottom: '1.5rem',
                        padding: '1rem',
                        backgroundColor: theme?.colors?.successLight || '#e8f5e8',
                        borderRadius: theme?.borderRadius || 6,
                        border: `1px solid ${theme?.colors?.success || '#4caf50'}`
                    }}>
                        <h3 style={{ margin: '0 0 0.5rem 0', color: theme?.colors?.text }}>{t('selectedOrder')}</h3>
                        <div style={{ fontSize: '0.9rem', color: theme?.colors?.textSecondary }}>
                            <div>{t('name')}: <strong>{selectedOrder.name}</strong></div>
                            <div>{t('table')}: <strong>{selectedOrder.table_name || t('unknown')}</strong></div>
                            <div>{t('items')}: <strong>{selectedOrderItems.length}</strong></div>
                            <div>{t('total')}: <strong>${selectedOrderTotal.toFixed(2)}</strong></div>
                        </div>

                        {/* Selected Order Items List */}
                        {selectedOrderItems.length > 0 && (
                            <div style={{ marginTop: '1rem' }}>
                                <h4 style={{ margin: '0 0 0.5rem 0', color: theme?.colors?.text }}>{t('itemsToBeMoved')}:</h4>
                                <div style={{
                                    maxHeight: '150px',
                                    overflowY: 'auto',
                                    border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                                    borderRadius: theme?.borderRadius || 4,
                                    padding: '0.5rem'
                                }}>
                                    {selectedOrderItems.map(item => {
                                        const itemPrice = Number(item.price || 0) * item.quantity;
                                        const discount = Number(item.discount || 0);
                                        const finalPrice = itemPrice - discount;

                                        return (
                                            <div
                                                key={item.id}
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'flex-start',
                                                    padding: '0.25rem',
                                                    margin: '0.25rem 0',
                                                    backgroundColor: theme?.colors?.surface || '#fff',
                                                    borderRadius: theme?.borderRadius || 4,
                                                    fontSize: '0.9rem'
                                                }}
                                            >
                                                <div style={{ flex: 1 }}>
                                                    <div>{item.product_name} x{item.quantity}</div>
                                                    {getOptionNames(item, allOptions) && getOptionNames(item, allOptions).length > 0 && (
                                                        <div style={{ fontSize: '0.8rem', color: theme?.colors?.textSecondary }}>
                                                            {getOptionNames(item, allOptions).join(', ')}
                                                        </div>
                                                    )}
                                                </div>
                                                <span style={{ fontWeight: 'bold', marginLeft: '0.5rem' }}>${finalPrice.toFixed(2)}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Combined Total */}
                {selectedOrderId && (
                    <div style={{
                        marginBottom: '1.5rem',
                        padding: '1rem',
                        backgroundColor: theme?.colors?.success || '#4caf50',
                        color: '#fff',
                        borderRadius: theme?.borderRadius || 6,
                        textAlign: 'center'
                    }}>
                        <h3 style={{ margin: '0', fontSize: '1.2rem' }}>
                            {t('combinedTotal')}: ${combinedTotal.toFixed(2)}
                        </h3>
                        <div style={{ fontSize: '0.9rem', marginTop: '0.25rem' }}>
                            ({orderItems.length + selectedOrderItems.length} {t('totalItems')})
                        </div>
                    </div>
                )}

                {/* Warning Message */}
                {selectedOrderId && (
                    <div style={{
                        marginBottom: '1.5rem',
                        padding: '1rem',
                        backgroundColor: theme?.colors?.warningBackground || '#fff3cd',
                        color: theme?.colors?.warningText || '#856404',
                        borderRadius: theme?.borderRadius || 6,
                        border: `1px solid ${theme?.colors?.warningBorder || '#ffeaa7'}`,
                        fontSize: '0.9rem'
                    }}>
                        <strong>⚠️ {t('warning')}:</strong> {t('combineWarningMessage')}
                    </div>
                )}

                {/* Error Message */}
                {error && (
                    <div style={{
                        marginBottom: '1rem',
                        padding: '0.5rem',
                        backgroundColor: theme?.colors?.errorBackground || '#ffebee',
                        color: theme?.colors?.errorText || '#c62828',
                        borderRadius: theme?.borderRadius || 4,
                        fontSize: '0.9rem',
                        textAlign: 'center'
                    }}>
                        {error}
                    </div>
                )}

                {/* Action Buttons */}
                <div style={{
                    display: 'flex',
                    gap: '1rem',
                    justifyContent: 'flex-end'
                }}>
                    <button
                        onClick={onClose}
                        disabled={loading}
                        style={{
                            backgroundColor: theme?.colors?.secondary || '#666',
                            color: theme?.colors?.surface || '#fff',
                            border: 'none',
                            borderRadius: theme?.borderRadius || 4,
                            padding: '0.75rem 1.5rem',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            fontSize: '1rem',
                            fontWeight: 'bold'
                        }}
                    >
                        {t('cancel')}
                    </button>
                    <button
                        onClick={handleCombineCheck}
                        disabled={loading || !selectedOrderId}
                        style={{
                            backgroundColor: (loading || !selectedOrderId) ? (theme?.colors?.disabled || '#ccc') : (theme?.colors?.success || '#4caf50'),
                            color: '#fff',
                            border: 'none',
                            borderRadius: theme?.borderRadius || 4,
                            padding: '0.75rem 1.5rem',
                            cursor: (loading || !selectedOrderId) ? 'not-allowed' : 'pointer',
                            fontSize: '1rem',
                            fontWeight: 'bold'
                        }}
                    >
                        {loading ? t('combining') : t('combineOrders')}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default CombineCheck;